import React, { useEffect, useRef, useState } from "react";
import { AnalyseResponse, BlockKey, CoachingDetail, CoachingNote, ReviseEmailResponse, RewriteHighlight } from "../../types";
import Tabs from "../components/Tabs";
import BigScoreCard from "../components/BigScoreCard";
import BlockGrid from "../components/BlockGrid";
import CoachingNoteCard from "../components/CoachingNoteCard";
import ScoreDelta from "../components/ScoreDelta";
import RewrittenEmailBody from "../components/RewrittenEmailBody";
import ReviseBox from "../components/ReviseBox";
import DiffView from "../components/DiffView";
import { apiFetch } from "../../office/apiClient";
import { copyText } from "../../office/clipboard";
import { setComposeSubject } from "../../office/composeBridge";
import styles from "./ResultsView.module.css";

type ResultsTab = "original" | "rewrite";

interface ResultsViewProps {
  result: AnalyseResponse;
  subject: string;
  originalDraft?: string;
  threadContext?: string;
  senderName?: string;
  recipientName?: string;
  onBack: () => void;
  onClose: () => void;
  // Insert the rewrite straight into the active Outlook compose draft.
  // Returns true on success; the caller is responsible for being a no-op
  // when the task pane is not attached to a compose item.
  onApplyToDraft?: (text: string) => Promise<boolean>;
  // True when an Outlook compose draft is available to write into. When false,
  // the "Insert into draft" button is hidden (manual entry / read mode).
  canApplyToDraft?: boolean;
}

const BLOCK_LABEL: Record<BlockKey, string> = {
  trustedPartner: "Trusted Partner",
  engage: "Engage",
  discover: "Discover",
  buildValue: "Build Value",
  advance: "Advance",
};

function rewriteToPlainText(result: AnalyseResponse): string {
  return result.rewrite.paragraphs.map((p) => p.text).join("\n\n");
}

function BackArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

export default function ResultsView({
  result,
  subject,
  originalDraft,
  threadContext,
  senderName,
  recipientName,
  onBack,
  onApplyToDraft,
  canApplyToDraft = false,
}: ResultsViewProps) {
  const [tab, setTab] = useState<ResultsTab>("original");
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [applyState, setApplyState] = useState<"idle" | "applied" | "error">("idle");
  const [subjectState, setSubjectState] = useState<"idle" | "copied" | "applied" | "error">("idle");
  const [rewriteView, setRewriteView] = useState<"ai" | "original">("ai");
  // "Rewrite with my changes": the user types instructions and we revise the
  // rewrite text in place (no re-scoring). revisedBody supersedes the original
  // rewrite for display, copy, and insert-into-draft once set.
  const [revisedBody, setRevisedBody] = useState<string | null>(null);
  const [revisedSubject, setRevisedSubject] = useState<string | null>(null);
  const [showReviseBox, setShowReviseBox] = useState(false);
  const [revising, setRevising] = useState(false);
  const [reviseError, setReviseError] = useState<string | null>(null);
  const [selectedNote, setSelectedNote] = useState<CoachingNote | null>(null);
  const [noteDetail, setNoteDetail] = useState<CoachingDetail | null>(null);
  const [noteDetailLoading, setNoteDetailLoading] = useState(false);
  const [noteDetailError, setNoteDetailError] = useState<string | null>(null);
  // Pre-seed the cache with details returned alongside the analyse response so
  // opening "Full coaching note" is instant. The per-click fetch below is only
  // used as a fallback for notes the backend didn't pre-generate (or for older
  // backends that don't yet return coachingDetails).
  const detailCacheRef = useRef<Map<string, CoachingDetail>>(new Map());
  const detailAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!result.coachingDetails || result.coachingDetails.length === 0) return;
    result.coachingDetails.forEach((detail, idx) => {
      const note = result.coachingNotes[idx];
      if (note) detailCacheRef.current.set(note.id, detail);
    });
  }, [result]);

  useEffect(() => {
    if (!selectedNote) {
      setNoteDetail(null);
      setNoteDetailError(null);
      setNoteDetailLoading(false);
      detailAbortRef.current?.abort();
      return;
    }

    const cached = detailCacheRef.current.get(selectedNote.id);
    if (cached) {
      setNoteDetail(cached);
      setNoteDetailLoading(false);
      setNoteDetailError(null);
      return;
    }

    detailAbortRef.current?.abort();
    const ctrl = new AbortController();
    detailAbortRef.current = ctrl;
    setNoteDetail(null);
    setNoteDetailError(null);
    setNoteDetailLoading(true);

    const draftForBackend = originalDraft ?? "";

    apiFetch("/api/coaching-detail/", {
      method: "POST",
      body: JSON.stringify({
        note: selectedNote,
        draftBody: draftForBackend,
        subject,
        threadContext,
        senderName,
        recipientName,
      }),
      signal: ctrl.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as CoachingDetail;
        detailCacheRef.current.set(selectedNote.id, data);
        setNoteDetail(data);
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === "AbortError") return;
        setNoteDetailError(
          err instanceof Error ? err.message : "Could not load the full coaching note.",
        );
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setNoteDetailLoading(false);
      });

    return () => ctrl.abort();
  }, [selectedNote, originalDraft, subject, threadContext, senderName, recipientName]);

  // The text that drives display, copy, and insert — the revised version once
  // the user has applied changes, otherwise the original ASLAN+ rewrite.
  const currentRewriteText = revisedBody ?? rewriteToPlainText(result);

  const handleCopy = async () => {
    const ok = await copyText(currentRewriteText);
    setCopyState(ok ? "copied" : "error");
    setTimeout(() => setCopyState("idle"), ok ? 1800 : 3000);
  };

  const suggestedSubject =
    revisedSubject ?? result.suggestedSubject ?? result.rewrite.suggestedSubject ?? null;

  const handleCopySubject = async () => {
    if (!suggestedSubject) return;
    const ok = await copyText(suggestedSubject);
    setSubjectState(ok ? "copied" : "error");
    setTimeout(() => setSubjectState("idle"), ok ? 1800 : 3000);
  };

  const handleApplySubject = async () => {
    if (!suggestedSubject) return;
    const ok = await setComposeSubject(suggestedSubject);
    setSubjectState(ok ? "applied" : "error");
    setTimeout(() => setSubjectState("idle"), ok ? 2200 : 3000);
  };

  const handleApply = async () => {
    if (!onApplyToDraft) return;
    const ok = await onApplyToDraft(currentRewriteText);
    setApplyState(ok ? "applied" : "error");
    setTimeout(() => setApplyState("idle"), 2200);
  };

  const handleRevise = async (instructions: string) => {
    setRevising(true);
    setReviseError(null);
    try {
      const res = await apiFetch("/api/revise-email/", {
        method: "POST",
        body: JSON.stringify({
          originalBody: currentRewriteText,
          instructions,
          subject: suggestedSubject ?? subject,
          threadContext,
          senderName,
          recipientName,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as ReviseEmailResponse;
      setRevisedBody(data.body);
      if (data.suggestedSubject) setRevisedSubject(data.suggestedSubject);
      setShowReviseBox(false);
    } catch (err: unknown) {
      setReviseError(err instanceof Error ? err.message : "Could not apply your changes. Try again.");
    } finally {
      setRevising(false);
    }
  };

  const handleTopBack = () => {
    if (selectedNote) {
      setSelectedNote(null);
      return;
    }
    onBack();
  };

  return (
    <div className={styles.panel}>
      <Tabs<ResultsTab>
        tabs={[
          { key: "original", label: "Original", icon: "●" },
          { key: "rewrite", label: "ASLAN+ Rewrite", icon: "✦" },
        ]}
        active={tab}
        onChange={setTab}
        leading={
          <button
            onClick={handleTopBack}
            aria-label={selectedNote ? "Back to coaching notes" : "Back"}
            title={selectedNote ? "Back to coaching notes" : "Back"}
            type="button"
          >
            <BackArrowIcon />
          </button>
        }
      />

      {tab === "original" ? (
        <div className={styles.body}>
          {selectedNote ? (
            <div className={styles.noteDetailContainer}>
              <div className={styles.noteDetailCard}>
                <div className={styles.noteDetailTitle}>{selectedNote.title}</div>
                <div className={styles.noteDetailMetaRow}>
                  <span className={`${styles.noteDetailBlockPill} ${styles[selectedNote.block]}`}>
                    {BLOCK_LABEL[selectedNote.block]}
                  </span>
                </div>

                {noteDetailLoading && (
                  <div className={styles.noteDetailLoading}>
                    <span className={styles.noteDetailSpinner} aria-hidden="true" />
                    Loading full coaching note…
                  </div>
                )}

                {!noteDetailLoading && noteDetailError && (
                  <div className={styles.noteDetailBody}>
                    {selectedNote.body}
                  </div>
                )}

                {!noteDetailLoading && noteDetail && (
                  <>
                    <div className={styles.noteDetailBody}>{noteDetail.summary}</div>

                    <div className={styles.noteDetailSection}>
                      <span className={styles.noteDetailSectionLabel}>Why it matters</span>
                      <div className={styles.noteDetailBody}>{noteDetail.whyItMatters}</div>
                    </div>

                    {noteDetail.whatToDo && noteDetail.whatToDo.length > 0 && (
                      <div className={styles.noteDetailSection}>
                        <span className={styles.noteDetailSectionLabel}>What to do</span>
                        <ul className={styles.noteDetailList}>
                          {noteDetail.whatToDo.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {noteDetail.example && (
                      <div className={styles.noteDetailSection}>
                        <span className={styles.noteDetailSectionLabel}>Example rewrite</span>
                        <div className={styles.noteDetailExample}>{noteDetail.example}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <BigScoreCard
                score={result.overallScore}
                verdict={result.overallVerdict}
                issuesFound={result.issuesFound}
              />

              <div className={styles.sectionRow}>
                <span className={styles.sectionLabel}>Score breakdown</span>
              </div>
              <BlockGrid
                blocks={result.blocks}
                selectedBlock={result.selectedBlock}
                trustedPartner={result.trustedPartner}
              />

              <div className={styles.sectionRow}>
                <span className={styles.sectionLabel}>Coaching notes</span>
                <span className={styles.countPill}>
                  {result.coachingNotes.length} item{result.coachingNotes.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className={styles.noteList}>
                {result.coachingNotes.map((note) => (
                  <CoachingNoteCard
                    key={note.id}
                    note={note}
                    actionLabel={note.severity === "positive" ? undefined : "Full coaching note"}
                    onAction={() => setSelectedNote(note)}
                  />
                ))}
              </div>

              <div className={styles.feedbackRow}>
                <span className={styles.feedbackLabel}>Was this coaching useful?</span>
                <div className={styles.feedbackBtns}>
                  <button
                    className={`${styles.thumbBtn} ${feedback === "up" ? styles.thumbActive : ""}`}
                    onClick={() => setFeedback("up")}
                    aria-label="Yes"
                    type="button"
                  >
                    👍
                  </button>
                  <button
                    className={`${styles.thumbBtn} ${feedback === "down" ? styles.thumbActive : ""}`}
                    onClick={() => setFeedback("down")}
                    aria-label="No"
                    type="button"
                  >
                    👎
                  </button>
                </div>
              </div>

              <button className={styles.primaryCta} onClick={() => setTab("rewrite")} type="button">
                <span>→ See ASLAN+ Rewrite</span>
              </button>
            </>
          )}
        </div>
      ) : result.insufficientContext ? (
        <div className={styles.body}>
          <div className={styles.insufficientCard}>
            <span className={styles.insufficientTitle}>Not enough to improve yet</span>
            {result.rewrite.paragraphs.map((p, i) => (
              <p key={i} className={styles.insufficientBody}>{p.text}</p>
            ))}
            {result.rewrite.caption && (
              <p className={styles.insufficientHint}>{result.rewrite.caption}</p>
            )}
          </div>
          <button className={styles.primaryCta} onClick={() => setTab("original")} type="button">
            <span>← Back to score</span>
          </button>
        </div>
      ) : (
        <div className={styles.body}>
          <ScoreDelta
            scoreBefore={result.rewrite.scoreBefore}
            scoreAfter={result.rewrite.scoreAfter}
            caption="Trusted Partner score improved"
          />

          {suggestedSubject && (
            <div className={styles.subjectCard}>
              <span className={styles.subjectLabel}>Suggested subject</span>
              <p className={styles.subjectText}>{suggestedSubject}</p>
              <div className={styles.subjectActions}>
                {canApplyToDraft && (
                  <button className={styles.subjectBtn} onClick={handleApplySubject} type="button">
                    {subjectState === "applied"
                      ? "✓ Applied"
                      : subjectState === "error"
                        ? "⚠ Couldn't apply"
                        : "↧ Use subject"}
                  </button>
                )}
                <button className={styles.subjectBtn} onClick={handleCopySubject} type="button">
                  {subjectState === "copied"
                    ? "✓ Copied"
                    : subjectState === "error" && !canApplyToDraft
                      ? "⚠ Couldn't copy"
                      : "Copy"}
                </button>
              </div>
            </div>
          )}

          <div className={styles.rewriteHeader}>
            <span className={styles.sectionLabel}>Rewritten email</span>
            <div className={styles.legend}>
              <button
                className={`${styles.legendChip} ${styles.legendAdded} ${rewriteView === "ai" ? styles.legendActive : ""}`}
                onClick={() => setRewriteView("ai")}
                type="button"
              >
                AI ADDED
              </button>
              <button
                className={`${styles.legendChip} ${styles.legendOriginal} ${rewriteView === "original" ? styles.legendActive : ""}`}
                onClick={() => setRewriteView("original")}
                type="button"
              >
                ORIGINAL
              </button>
            </div>
          </div>

          {rewriteView === "original" ? (
            <div className={styles.originalDraftContainer}>
              {originalDraft ? (
                <pre className={styles.originalDraftText}>{originalDraft}</pre>
              ) : (
                <p className={styles.originalDraftPlaceholder}>No draft captured</p>
              )}
            </div>
          ) : rewriteView === "ai" && originalDraft ? (
            <DiffView original={originalDraft} rewritten={currentRewriteText} mode="additions-only" />
          ) : (
            <RewrittenEmailBody
              paragraphs={
                revisedBody
                  ? revisedBody.split(/\n{2,}/).map((t): RewriteHighlight => ({ text: t, kind: "original" }))
                  : result.rewrite.paragraphs
              }
            />
          )}

          <div className={styles.sectionRow}>
            <span className={styles.sectionLabel}>What would you like to do?</span>
          </div>
          <div className={styles.actionRow}>
            {canApplyToDraft && onApplyToDraft && (
              <button
                className={`${styles.actionBtn} ${styles.actionPrimary}`}
                onClick={handleApply}
                type="button"
                title="Replace the current Outlook draft with this rewrite"
              >
                {applyState === "applied"
                  ? "✓ Inserted into Draft"
                  : applyState === "error"
                    ? "⚠ Could not insert"
                    : "↧ Insert into Draft"}
              </button>
            )}
            <button
              className={`${styles.actionBtn} ${canApplyToDraft && onApplyToDraft ? styles.actionSecondary : styles.actionPrimary}`}
              onClick={handleCopy}
              type="button"
            >
              {copyState === "copied"
                ? "✓ Copied!"
                : copyState === "error"
                  ? "⚠ Couldn't copy — select & copy manually"
                  : "✓ Copy to Clipboard"}
            </button>
            <button
              className={`${styles.actionBtn} ${styles.actionSecondary}`}
              onClick={() => {
                setReviseError(null);
                setShowReviseBox((v) => !v);
              }}
              type="button"
            >
              ↻ Rewrite with My Changes
            </button>
            <button
              className={`${styles.actionBtn} ${styles.actionSecondary}`}
              onClick={() => setTab("original")}
              type="button"
            >
              ✕ Ignore
            </button>
          </div>

          {showReviseBox && (
            <ReviseBox
              busy={revising}
              error={reviseError}
              onApply={handleRevise}
              onCancel={() => {
                setShowReviseBox(false);
                setReviseError(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
